import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ApiError,
  api,
  can,
  codeWords,
  readIf,
  refusalReason,
  shortDate,
  termPlaceWords,
  valueLabel,
  type CatalogueValue,
  type ContractGap,
  type ContractTerm,
  type ContractTermSet,
  type DocumentFinding,
  type DocumentFindingSet,
} from '../../lib/api'
import { useSave } from '../../lib/save'
import { DataTable, ErrorBox, Loading, type Column } from '../DataTable'
import { Dialog, Status, Toggletip, type Tone } from '../kit'
import {
  BAND_CODES,
  EDIT_UNITS,
  KIND_WORDS,
  READ_AGAIN_WORDS,
  TERM_WORDS,
  batchesFor,
  driftSides,
  editable,
  highReadings,
  matchWord,
  refusalAsks,
  termValue,
  type DecideBatch,
} from './contractTerms'
import { hashFor } from './sections'
import '../../styles/docintel.css'

// What a vendor's documents say, in two boxes that never mix (release 3.3,
// documents lane D4).
//
// The first box is personal data VALUES sitting inside the files: an Aadhaar
// number in an annex, a signatory's mobile. They are shown masked, with where
// and in what context; the values themselves are never kept anywhere.
//
// The second box is what the contract SAYS the vendor will handle: the kinds of
// data, whose, why, where, through whom, for how long, how fast a breach is
// reported. Each line cites its clause, and nothing reaches the vendor's record
// until a person accepts it.
//
// Mixing them is the error this layout exists to prevent: "Aadhaar" in the
// first box is a number somebody pasted into a contract; "Aadhaar" in the
// second is a promise about what the vendor will process.

const CONTEXT_WORDS: Record<string, string> = {
  DATA_SAMPLE: 'Data sample',
  IN_TEXT: 'In the text',
  SIGNATORY: 'Signatory or contact',
  PARTY_ID: 'Party identifier',
}

const STATUS_WORDS: Record<string, [Tone, string]> = {
  OPEN: ['warn', 'Needs a decision'],
  EXPECTED: ['plain', 'Expected'],
  REDACTION_REQUESTED: ['info', 'Redacted copy asked for'],
  RESTRICTED: ['info', 'Document restricted'],
}

function plural(n: number, one: string, many: string): string {
  return n + ' ' + (n === 1 ? one : many)
}

// What a row shows in place of the words of a document restricted to evidence
// readers (review D2). The row itself is still counted and decided on.
const WITHHELD_WORDS = 'Restricted to evidence readers'

// A reading as one phrase, for a button that acts on it.
function termWords(t: ContractTerm): string {
  return (TERM_WORDS[t.kind] ?? codeWords(t.kind)) + ': ' + termValue(t)
}

// What a refused request says, in the words the dialog shows beside the field.
function problemOf(err: unknown): { words: string; field: 'value' | 'note' | '' } {
  if (err instanceof ApiError) {
    const issue = err.issues[0]
    if (issue) return { words: issue.detail, field: issue.code === 'INVALID_VALUE' ? 'value' : issue.code === 'NOTE_REQUIRED' || issue.code === 'TOO_LONG' ? 'note' : '' }
    return { words: err.message, field: '' }
  }
  return { words: 'Not recorded. Try again.', field: '' }
}

export function DocumentIntelligence({ vendorId, onRecordChanged }: { vendorId: string; onRecordChanged?: () => void }) {
  const [findings, setFindings] = useState<DocumentFindingSet | null>(null)
  const [findingsError, setFindingsError] = useState<unknown>(null)
  const [terms, setTerms] = useState<ContractTermSet | null>(null)
  const [termsError, setTermsError] = useState<unknown>(null)
  const [context, setContext] = useState<string>('')
  const [termView, setTermView] = useState<'decide' | 'accepted' | 'gaps' | 'all'>('decide')
  const [busy, setBusy] = useState<string | null>(null)
  const [actionError, setActionError] = useState<unknown>(null)
  const [actionNote, setActionNote] = useState('')
  // A document read again since the person looked: the terms are reloaded and
  // the person is told to decide on the new reading.
  const [readAgain, setReadAgain] = useState(false)
  // Decisions waiting for a subject count band, because accepting them would
  // start the vendor's first data record.
  const [bandAsk, setBandAsk] = useState<DecideBatch[] | null>(null)
  // The API's words for why it asks, and whether NONE is the problem.
  const [bandWhy, setBandWhy] = useState('')
  const [bandNotNone, setBandNotNone] = useState(false)
  const bandPick = useRef<HTMLSelectElement>(null)
  const [band, setBand] = useState('')
  const [bands, setBands] = useState<CatalogueValue[]>(BAND_CODES.map((code) => ({ code, why: '' })))
  // A reading accepted with a corrected value, which needs a note.
  const [editing, setEditing] = useState<ContractTerm | null>(null)
  const [editValue, setEditValue] = useState('')
  const [editNote, setEditNote] = useState('')
  const [editProblem, setEditProblem] = useState('')
  // Which field the problem is about, so only that one is marked invalid.
  const [editProblemField, setEditProblemField] = useState<'value' | 'note' | ''>('')
  const [confirmHigh, setConfirmHigh] = useState(false)
  // A gap being dismissed, with the reason the person gives.
  const [dismissing, setDismissing] = useState<ContractGap | null>(null)
  const [dismissReason, setDismissReason] = useState('')
  const [dismissProblem, setDismissProblem] = useState('')
  // The two dialogs that send something send through a save (lib/save.ts), so
  // a dialog closed while its request is out never shows another record's
  // answer, and stays busy while it is out.
  const editSave = useSave(editing ? editing.documentId + ' ' + editing.key : null, 'Accept with a corrected value')
  const dismissSave = useSave(dismissing ? dismissing.code : null, 'Dismiss a contract gap')

  // A good reload clears the error a failed one left (review D45, 13).
  const load = useCallback(() => {
    api.vendorDocumentFindings(vendorId).then((f) => {
      setFindings(f)
      setFindingsError(null)
    }, setFindingsError)
    api.vendorContractTerms(vendorId).then((t) => {
      setTerms(t)
      setTermsError(null)
    }, setTermsError)
  }, [vendorId])
  useEffect(load, [load])

  // The band question takes the keyboard when it appears, and the select it
  // lands on is described by the question, so it is read out.
  useEffect(() => {
    if (bandAsk !== null) bandPick.current?.focus?.()
  }, [bandAsk])

  const mayDecide = can('evidence.review')
  // Accepting writes the vendor's data record, so it also needs vendor.edit;
  // the API holds the same rule.
  const mayAccept = mayDecide && can('vendor.edit')
  const acceptWhy = mayAccept ? undefined : refusalReason(mayDecide ? 'vendor.edit' : 'evidence.review')

  function decideFinding(f: DocumentFinding, decision: string) {
    setBusy(f.id)
    setActionError(null)
    api
      .decideDocumentFinding(f.id, decision)
      .then((r) => {
        setActionNote(r.note)
        load()
      })
      .catch(setActionError)
      .finally(() => setBusy(null))
  }

  // One request per document, each naming the reading the person looked at.
  // A refusal that asks for a band keeps the batches not yet recorded for the
  // band prompt; one that says the document was read again reloads the terms.
  // Any other refusal is returned, and shown in the panel unless the caller
  // shows it itself (quiet).
  async function submit(batches: DecideBatch[], withBand?: string, quiet = false): Promise<unknown> {
    setBusy('terms')
    setActionError(null)
    setActionNote('')
    setReadAgain(false)
    let accepted = false
    let failed: unknown = null
    for (const [i, b] of batches.entries()) {
      try {
        const r = await api.decideContractTerms(b.documentId, b.runId, b.decisions, withBand)
        // The tier the accepted facts re-ran, in one short line.
        setActionNote(r.tier ? r.note + ' Tier now ' + r.tier.tierLabel + '.' : r.note)
        if (b.decisions.some((d) => d.decision === 'ACCEPT')) accepted = true
      } catch (err) {
        const asks = refusalAsks(err)
        if (asks === 'band') askForBand(batches.slice(i), err)
        else if (asks === 'readAgain') setReadAgain(true)
        else if (quiet) failed = err
        else setActionError(err)
        break
      }
    }
    setBusy(null)
    load()
    if (accepted) onRecordChanged?.()
    return failed
  }

  function askForBand(waiting: DecideBatch[], err: unknown) {
    const issue = err instanceof ApiError ? err.issues.find((i) => i.field === 'subjectCountBand') : undefined
    setBandWhy(
      issue?.detail ||
        'This vendor has no data record yet. Say how many people the data is about, and the accepted readings start the record.',
    )
    setBandNotNone(issue?.code === 'INCONSISTENT')
    setBand('')
    setBandAsk(waiting)
    // The words the data record editor uses, where this reader may read them.
    readIf('config.read', () => api.dataElementCatalogue(), null)
      .then((cat) => {
        if (cat && cat.subjectCountBands.length > 0) setBands(cat.subjectCountBands)
      })
      .catch(() => undefined)
  }

  function decideTerm(t: ContractTerm, decision: 'ACCEPT' | 'REJECT') {
    if (!terms) return
    void submit(batchesFor(terms, [{ term: t, decision: { key: t.key, decision } }]))
  }

  function startEdit(t: ContractTerm) {
    setEditing(t)
    setEditValue(t.value ?? '')
    setEditNote('')
    setEditProblem('')
    setEditProblemField('')
  }

  // A changed value is an internal assertion rather than a reading of the
  // clause, so it needs a note; the API holds the same rule. The dialog stays
  // open until the request returns, and shows a refusal beside the field.
  function saveEdit() {
    if (!editing || !terms) return
    const value = editValue.trim()
    const note = editNote.trim()
    if (value === '') {
      setEditProblem('Type the value the contract should be read as.')
      setEditProblemField('value')
      return
    }
    if (value !== (editing.value ?? '') && note === '') {
      setEditProblem('Say why the value is different. A changed value is recorded as your own assertion, so it needs a reason.')
      setEditProblemField('note')
      return
    }
    setEditProblem('')
    setEditProblemField('')
    const t = editing
    const sent = submit(batchesFor(terms, [{ term: t, decision: { key: t.key, decision: 'ACCEPT', value, note } }]), undefined, true)
    editSave.send(
      sent.then((failed) => {
        if (failed != null) throw failed
      }),
      {
        done: () => setEditing(null),
        failed: (err) => {
          const problem = problemOf(err)
          setEditProblem(problem.words)
          setEditProblemField(problem.field)
        },
      },
    )
  }

  function acceptAllHigh() {
    if (!terms) return
    setConfirmHigh(false)
    const items = highReadings(terms).map((t) => ({ term: t, decision: { key: t.key, decision: 'ACCEPT' as const } }))
    void submit(batchesFor(terms, items))
  }

  // Either way the terms are read again: a raised gap names its finding, a
  // dismissed one leaves the list. Returns the refusal, if any, when quiet.
  async function gapAction(call: () => Promise<{ note: string }>, quiet = false): Promise<unknown> {
    setBusy('gap')
    setActionError(null)
    setActionNote('')
    setReadAgain(false)
    let failed: unknown = null
    try {
      setActionNote((await call()).note)
    } catch (err) {
      if (quiet) failed = err
      else setActionError(err)
    }
    setBusy(null)
    load()
    return failed
  }

  function startDismiss(g: ContractGap) {
    setDismissing(g)
    setDismissReason('')
    setDismissProblem('')
  }

  function saveDismiss() {
    if (!dismissing) return
    const reason = dismissReason.trim()
    if (reason === '') {
      setDismissProblem('Say why the gap does not apply. A dismissal with no reason reads as a gap nobody looked at.')
      return
    }
    setDismissProblem('')
    const code = dismissing.code
    const sent = gapAction(() => api.dismissContractGap(vendorId, code, reason), true)
    dismissSave.send(
      sent.then((failed) => {
        if (failed != null) throw failed
      }),
      { done: () => setDismissing(null), failed: (err) => setDismissProblem(problemOf(err).words) },
    )
  }

  const mayRaise = can('finding.manage')

  const titleOf = (documentId: string) => terms?.documents.find((d) => d.documentId === documentId)?.title ?? 'Document'

  const shownFindings = useMemo(
    () => (findings?.findings ?? []).filter((f) => (context === '' ? f.context !== 'PARTY_ID' : f.context === context)),
    [findings, context],
  )

  const findingColumns: Column<DocumentFinding>[] = [
    {
      key: 'kind',
      header: 'Type',
      sortValue: (f) => f.kind,
      render: (f) => <span>{KIND_WORDS[f.kind] ?? codeWords(f.kind)}</span>,
    },
    { key: 'count', header: 'Count', numeric: true, sortValue: (f) => f.count, render: (f) => <span>{f.count}</span> },
    {
      key: 'place',
      header: 'Where',
      sortValue: (f) => (f.documentTitle ?? '') + f.place,
      render: (f) => (
        <>
          <span>{f.documentTitle}</span>
          {!f.withheld && <span className="cell-sub">{f.place}</span>}
        </>
      ),
    },
    {
      key: 'sample',
      header: 'Sample (masked)',
      render: (f) =>
        f.withheld ? <span className="cell-sub">{WITHHELD_WORDS}</span> : <span className="kit-mono">{f.maskedSample}</span>,
    },
    {
      key: 'context',
      header: 'Kind',
      sortValue: (f) => f.context,
      render: (f) => (
        <>
          <span>{CONTEXT_WORDS[f.context] ?? f.context}</span>
          <span className="cell-sub">{f.readBy === 'ocr' ? 'Read by OCR' : 'Read from the file'}</span>
        </>
      ),
    },
    {
      key: 'status',
      header: 'Decision',
      sortValue: (f) => f.status,
      render: (f) => {
        if (f.partyIdentifier) return <span className="cell-sub">Not personal data</span>
        const [tone, word] = STATUS_WORDS[f.status] ?? ['neutral', f.status]
        return (
          <div className="di-actions">
            <Status tone={tone}>{word}</Status>
            {f.status === 'OPEN' && (
              <span className="chiprow">
                <button
                  type="button"
                  disabled={!mayDecide || busy === f.id}
                  title={mayDecide ? undefined : refusalReason('evidence.review')}
                  aria-label={'Expected: ' + (KIND_WORDS[f.kind] ?? codeWords(f.kind)) + ' in ' + (f.documentTitle ?? 'the document')}
                  onClick={() => decideFinding(f, 'EXPECTED')}
                >
                  Expected
                </button>
                <button
                  type="button"
                  disabled={!mayDecide || busy === f.id}
                  title={mayDecide ? undefined : refusalReason('evidence.review')}
                  aria-label={'Ask for a redacted copy: ' + (KIND_WORDS[f.kind] ?? codeWords(f.kind)) + ' in ' + (f.documentTitle ?? 'the document')}
                  onClick={() => decideFinding(f, 'REDACTION_REQUESTED')}
                >
                  Ask for a redacted copy
                </button>
                <button
                  type="button"
                  disabled={!mayDecide || busy === f.id}
                  title={mayDecide ? undefined : refusalReason('evidence.review')}
                  aria-label={'Restrict the document: ' + (KIND_WORDS[f.kind] ?? codeWords(f.kind)) + ' in ' + (f.documentTitle ?? 'the document')}
                  onClick={() => decideFinding(f, 'RESTRICTED')}
                >
                  Restrict the document
                </button>
              </span>
            )}
          </div>
        )
      },
    },
  ]

  const shownTerms = useMemo(() => {
    const all = terms?.proposals ?? []
    switch (termView) {
      case 'decide':
        return all.filter((t) => t.status === 'PROPOSED')
      case 'accepted':
        return all.filter((t) => t.status === 'ACCEPT')
      case 'all':
        return all
    }
    return []
  }, [terms, termView])

  const termColumns: Column<ContractTerm>[] = [
    {
      key: 'item',
      header: 'Item',
      sortValue: (t) => t.kind + t.code,
      render: (t) => (
        <>
          <span>{TERM_WORDS[t.kind] ?? codeWords(t.kind)}</span>
          <span className="cell-sub">{termValue(t)}</span>
        </>
      ),
    },
    {
      key: 'says',
      header: 'The contract says',
      render: (t) => (t.withheld ? <span className="cell-sub">{WITHHELD_WORDS}</span> : <TwoLines text={t.snippet} />),
    },
    {
      key: 'where',
      header: 'Where',
      sortValue: (t) => t.locator,
      render: (t) => (
        <>
          <span>{titleOf(t.documentId)}</span>
          <span className="cell-sub">{termPlaceWords(t)}</span>
        </>
      ),
    },
    {
      key: 'match',
      header: 'Match',
      sortValue: (t) => t.confidence,
      render: (t) => {
        const [tone, word] = matchWord(t.confidence)
        return <Status tone={tone}>{word}</Status>
      },
    },
    {
      key: 'decision',
      header: 'Decision',
      render: (t) => {
        if (t.status !== 'PROPOSED') {
          const d = t.decision
          const said = t.status === 'REJECT' ? 'Rejected' : d?.value ? 'Accepted with a corrected value' : 'Accepted'
          return (
            <div className="di-actions">
              <Status tone={t.status === 'ACCEPT' ? 'ok' : 'plain'}>{said}</Status>
              {d && <span className="cell-sub">{[d.decidedBy, shortDate(d.decidedAt)].filter(Boolean).join(', ')}</span>}
              {d?.carried && <span className="cell-sub">Decided on an earlier reading</span>}
            </div>
          )
        }
        const off = !mayDecide || busy !== null
        const why = mayDecide ? undefined : refusalReason('evidence.review')
        const item = termWords(t)
        return (
          <span className="chiprow">
            <button
              type="button"
              className="primary"
              disabled={off || !mayAccept}
              title={acceptWhy}
              aria-label={'Accept ' + item}
              onClick={() => decideTerm(t, 'ACCEPT')}
            >
              Accept
            </button>
            {editable(t) && (
              <button
                type="button"
                disabled={off || !mayAccept}
                title={acceptWhy}
                aria-label={'Edit ' + item}
                onClick={() => startEdit(t)}
              >
                Edit
              </button>
            )}
            <button type="button" disabled={off} title={why} aria-label={'Reject ' + item} onClick={() => decideTerm(t, 'REJECT')}>
              Reject
            </button>
          </span>
        )
      },
    },
  ]

  const gapColumns: Column<ContractGap>[] = [
    { key: 'code', header: 'Gap', sortValue: (g) => g.code, render: (g) => <span className="kit-mono">{g.code}</span> },
    {
      key: 'title',
      header: 'What is missing',
      render: (g) => (
        <>
          <span>{g.title}</span>
          {g.partial && <span className="cell-sub">Not every document read in full</span>}
        </>
      ),
    },
    {
      key: 'detail',
      header: 'Why it matters',
      render: (g) => {
        // Drift: the two sides, one short line each.
        const sides = g.code === 'G7' ? driftSides(g.detail) : null
        if (sides && sides.contract.length + sides.record.length > 0) {
          return (
            <span className="di-sides">
              {sides.contract.length > 0 && <span>Contract names {sides.contract.join(', ')}</span>}
              {sides.record.length > 0 && <span>Data record has {sides.record.join(', ')}</span>}
            </span>
          )
        }
        return <TwoLines text={g.detail} />
      },
    },
    {
      key: 'decision',
      header: 'Decision',
      render: (g) => {
        if (g.decision?.state === 'RAISED') {
          return (
            <div className="di-actions">
              <Status tone="info">Raised as a finding</Status>
              <a href={hashFor(vendorId, { section: 'risk', part: 'findings' })}>Contract gap {g.code} in Findings</a>
              <span className="cell-sub">{shortDate(g.decision.at)}</span>
            </div>
          )
        }
        return (
          <span className="chiprow">
            {mayRaise && (
              <button
                type="button"
                className="primary"
                disabled={busy !== null}
                aria-label={'Raise finding for gap ' + g.code}
                onClick={() => void gapAction(() => api.raiseContractGap(vendorId, g.code))}
              >
                Raise finding
              </button>
            )}
            <button
              type="button"
              disabled={!mayDecide || busy !== null}
              title={mayDecide ? undefined : refusalReason('evidence.review')}
              aria-label={'Dismiss gap ' + g.code}
              onClick={() => startDismiss(g)}
            >
              Dismiss
            </button>
          </span>
        )
      },
    },
  ]

  const high = terms ? highReadings(terms) : []
  const uncounted = (terms?.documents ?? []).filter((d) => !d.counts || !d.termsRead)

  const unscanned = (findings?.documents ?? []).filter((d) => d.scan !== 'Scanned')
  const byContext = findings?.byContext ?? {}
  const personal = (byContext.DATA_SAMPLE ?? 0) + (byContext.IN_TEXT ?? 0) + (byContext.SIGNATORY ?? 0)
  // Both boxes may say some documents are restricted; it is said once.
  const restrictedNote = terms?.restrictedNote || findings?.restrictedNote || ''

  return (
    <section className="panel di-panel" aria-label="What the documents say">
      {actionError != null && <ErrorBox error={actionError} what="The decision" verb="recorded" />}
      {actionNote !== '' && (
        <p className="note sw-row" role="status">
          {actionNote}
        </p>
      )}
      {readAgain && (
        <p className="note sw-row" role="status">
          {READ_AGAIN_WORDS}
        </p>
      )}
      {restrictedNote !== '' && <p className="note sw-row">{restrictedNote}</p>}

      <div className="di-box" aria-labelledby="di-a">
        <div className="section-head" id="di-a">
          Personal data found inside these documents <span className="mono">{personal} values</span>
          <Toggletip label="What this box shows">
            Real values found in the files, shown masked and never kept. A company's PAN or GSTIN is listed apart
            and not counted. Bank accounts are found only where labelled as such.
          </Toggletip>
        </div>
        {findingsError != null && <ErrorBox error={findingsError} what="The personal data found" />}
        {findings === null && findingsError == null && <Loading what="the personal data found" />}
        {findings !== null && (
          <>
            <div className="chiprow di-filters" role="group" aria-label="Show">
              {(
                [
                  ['', 'All personal data', personal],
                  ['DATA_SAMPLE', 'Data samples', byContext.DATA_SAMPLE ?? 0],
                  ['IN_TEXT', 'In the text', byContext.IN_TEXT ?? 0],
                  ['SIGNATORY', 'Signatory details', byContext.SIGNATORY ?? 0],
                  ['PARTY_ID', 'Party identifiers', byContext.PARTY_ID ?? 0],
                ] as [string, string, number][]
              ).map(([key, label, n]) => (
                <button key={key || 'all'} type="button" aria-pressed={context === key} onClick={() => setContext(key)}>
                  {label} <span className="mono">{n}</span>
                </button>
              ))}
            </div>
            {unscanned.length > 0 && (
              <ul className="di-unscanned">
                {unscanned.map((d) => (
                  <li key={d.documentId}>
                    <Status tone="warn">Not scanned</Status> {d.title}: <span className="cell-sub">{d.readNote || d.scan}</span>
                  </li>
                ))}
              </ul>
            )}
            <DataTable
              caption="Personal data values found inside this vendor's documents, masked"
              columns={findingColumns}
              rows={shownFindings}
              rowKey={(f) => f.id}
              empty={{
                title: context === '' ? 'No personal data value found in the documents scanned' : 'None of this kind',
                detail:
                  unscanned.length > 0
                    ? 'Some documents were not scanned; they are listed above. Nothing is said about them.'
                    : 'Every document on file was read and scanned.',
              }}
            />
          </>
        )}
      </div>

      <div className="di-box" aria-labelledby="di-b">
        <div className="section-head" id="di-b">
          What the contract says the vendor will handle
          <Toggletip label="What this box shows">
            Read from the contracts with a fixed word list. Each line names its clause. Nothing reaches the vendor's
            record until a person accepts it; the match word only sorts the list.
          </Toggletip>
        </div>
        {termsError != null && <ErrorBox error={termsError} what="The contract terms" />}
        {terms === null && termsError == null && <Loading what="the contract terms" />}
        {terms !== null && (
          <>
            <div className="chiprow di-filters" role="group" aria-label="Show">
              {(
                [
                  ['decide', 'Needs decision', terms.counts.needsDecision],
                  ['accepted', 'Accepted', terms.counts.accepted],
                  ['gaps', 'Gaps', terms.counts.gaps],
                  ['all', 'All', terms.proposals.length],
                ] as ['decide' | 'accepted' | 'gaps' | 'all', string, number][]
              ).map(([key, label, n]) => (
                <button key={key} type="button" aria-pressed={termView === key} onClick={() => setTermView(key)}>
                  {label} <span className="mono">{n}</span>
                </button>
              ))}
              {termView === 'decide' && high.length > 0 && (
                <button
                  type="button"
                  className="primary"
                  disabled={!mayAccept || busy !== null}
                  title={acceptWhy}
                  onClick={() => setConfirmHigh(true)}
                >
                  Accept all High <span className="mono">{high.length}</span>
                </button>
              )}
            </div>
            {bandAsk !== null && (
              <div className="di-band" role="group" aria-labelledby="di-band-q">
                <p id="di-band-q" role="alert">
                  {bandWhy}
                </p>
                <label htmlFor="di-band-pick">Number of data subjects</label>
                <select
                  id="di-band-pick"
                  ref={bandPick}
                  value={band}
                  aria-describedby="di-band-q"
                  onChange={(e) => setBand(e.target.value)}
                >
                  <option value="">Choose a band</option>
                  {bands.filter((b) => !bandNotNone || b.code !== 'NONE').map((b) => (
                    <option key={b.code} value={b.code}>
                      {valueLabel(b)}
                    </option>
                  ))}
                </select>
                <p className="field-note">How many people, not how many records</p>
                <span className="chiprow">
                  <button
                    type="button"
                    className="primary"
                    disabled={band === '' || busy !== null}
                    onClick={() => {
                      const waiting = bandAsk
                      setBandAsk(null)
                      void submit(waiting, band)
                    }}
                  >
                    Accept with this band
                  </button>
                  <button type="button" onClick={() => setBandAsk(null)}>
                    Cancel
                  </button>
                </span>
              </div>
            )}
            {(terms.vendorCorrections ?? []).length > 0 && (
              <ul className="di-unscanned" aria-label="Answers the vendor changed">
                {(terms.vendorCorrections ?? []).map((c) => (
                  <li key={c.reference + c.questionKey + c.at}>
                    <Status tone="warn">
                      {c.provenance === 'DOCUMENT_EXTRACTED'
                        ? 'Vendor disagrees with the contract reading'
                        : 'Vendor disagrees with what was filled in'}
                    </Status>{' '}
                    {c.prompt || 'A question'} on {c.reference}. Was <strong>{c.wasWords || c.was || 'blank'}</strong>, now{' '}
                    <strong>{c.notApplicable ? 'Not applicable' : c.nowWords || c.now || 'blank'}</strong>.{' '}
                    {c.readFrom && <span className="cell-sub">Read from {c.readFrom}.</span>}
                  </li>
                ))}
              </ul>
            )}
            {uncounted.length > 0 && (
              <ul className="di-unscanned" aria-label="Documents not read for their terms">
                {uncounted.map((d) =>
                  d.counts ? (
                    <li key={d.documentId}>
                      <Status tone="warn">Not read for terms</Status> {d.title}:{' '}
                      <span className="cell-sub">{d.readNote || 'Not read yet.'}</span>
                    </li>
                  ) : (
                    <li key={d.documentId}>
                      <Status tone="plain">Not counted</Status> {d.title}: <span className="cell-sub">{d.termsNote}</span>
                    </li>
                  ),
                )}
              </ul>
            )}
            {termView === 'gaps' ? (
              <DataTable
                caption="Contract gaps the readings point to"
                columns={gapColumns}
                rows={terms.gaps}
                rowKey={(g) => g.code}
                empty={{ title: 'No contract gap', detail: 'Every rule G1 to G7 is met by what the documents say.' }}
              />
            ) : (
              <DataTable
                caption="What the contract says, one reading per line, with its clause"
                columns={termColumns}
                rows={shownTerms}
                rowKey={(t) => t.documentId + t.key}
                empty={{
                  title: termView === 'decide' ? 'Nothing waiting for a decision' : 'Nothing here',
                  detail:
                    terms.documents.length === 0
                      ? 'No document is on file. Upload the signed MSA and DPA in Contracts below.'
                      : plural(terms.documents.filter((d) => d.termsRead).length, 'document has', 'documents have') + ' been read for terms.',
                }}
              />
            )}
          </>
        )}
      </div>

      <Dialog
        shape="modal"
        kind="form"
        open={editing !== null}
        onClose={() => setEditing(null)}
        title="Accept with a corrected value"
        busy={editSave.busy}
        problems={editProblem !== '' ? 1 : 0}
        primary={{ label: editSave.busy ? 'Recording' : 'Accept with this value', onClick: saveEdit }}
      >
        {editing && (
          <div className="vform">
            <p>
              {TERM_WORDS[editing.kind] ?? codeWords(editing.kind)}: the contract reads{' '}
              <strong>{termValue(editing)}</strong> in {titleOf(editing.documentId)}, {editing.locator}.
            </p>
            <div className="field">
              <label htmlFor="di-edit-value">Corrected value ({EDIT_UNITS[editing.kind]})</label>
              <input
                id="di-edit-value"
                value={editValue}
                aria-describedby={editProblemField === 'value' ? 'di-edit-problem' : undefined}
                aria-invalid={editProblemField === 'value' ? true : undefined}
                onChange={(e) => setEditValue(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="di-edit-note">Why it differs from the contract</label>
              <textarea
                id="di-edit-note"
                value={editNote}
                aria-describedby={'di-edit-why' + (editProblemField === 'note' ? ' di-edit-problem' : '')}
                aria-invalid={editProblemField === 'note' ? true : undefined}
                onChange={(e) => setEditNote(e.target.value)}
              />
              <p className="field-note" id="di-edit-why">
                Needed when the value changes. A corrected value is recorded as your own assertion, not as a reading
                of the clause.
              </p>
            </div>
            {editProblem !== '' && (
              <p className="note" role="alert" id="di-edit-problem">
                {editProblem}
              </p>
            )}
          </div>
        )}
      </Dialog>

      <Dialog
        shape="modal"
        kind="form"
        open={dismissing !== null}
        onClose={() => setDismissing(null)}
        title={dismissing ? 'Dismiss gap ' + dismissing.code : 'Dismiss gap'}
        busy={dismissSave.busy}
        problems={dismissProblem !== '' ? 1 : 0}
        primary={{ label: dismissSave.busy ? 'Recording' : 'Dismiss the gap', onClick: saveDismiss }}
      >
        {dismissing && (
          <div className="vform">
            <p>
              <strong>{dismissing.title}.</strong> {dismissing.detail}
            </p>
            <div className="field">
              <label htmlFor="di-dismiss-why">Why the gap does not apply</label>
              <textarea
                id="di-dismiss-why"
                value={dismissReason}
                aria-describedby={'di-dismiss-note' + (dismissProblem !== '' ? ' di-dismiss-problem' : '')}
                aria-invalid={dismissProblem !== '' ? true : undefined}
                onChange={(e) => setDismissReason(e.target.value)}
              />
              <p className="field-note" id="di-dismiss-note">
                Required. The gap stays dismissed until a document is read again.
              </p>
            </div>
            {dismissProblem !== '' && (
              <p className="note" role="alert" id="di-dismiss-problem">
                {dismissProblem}
              </p>
            )}
          </div>
        )}
      </Dialog>

      <Dialog
        shape="drawer"
        kind="form"
        open={confirmHigh}
        onClose={() => setConfirmHigh(false)}
        title="Accept every High match"
        primary={{ label: 'Accept these ' + high.length, off: high.length === 0 && 'Nothing is a High match now', onClick: acceptAllHigh }}
      >
        <p>
          {plural(high.length, 'reading goes', 'readings go')} on the vendor's record, each with the clause it came
          from. Nothing else is accepted.
        </p>
        <ul className="di-unscanned" aria-label="Readings to accept">
          {high.map((t) => (
            <li key={t.documentId + t.key}>
              {TERM_WORDS[t.kind] ?? codeWords(t.kind)}: {termValue(t)}{' '}
              <span className="cell-sub">
                {titleOf(t.documentId)}, {termPlaceWords(t)}
              </span>
            </li>
          ))}
        </ul>
      </Dialog>
    </section>
  )
}

// Two lines of a long quotation, with the rest one press away.
function TwoLines({ text }: { text: string }) {
  const [open, setOpen] = useState(false)
  if (text.length <= 160) return <span>{text}</span>
  return (
    <span>
      {open ? text : text.slice(0, 160).trimEnd() + '...'}{' '}
      <button type="button" className="linkish" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        {open ? 'Less' : 'More'}
      </button>
    </span>
  )
}
